import { Stroke, Style } from "ol/style";
import { extractTags } from "./propertyExtraction";
import { styleBySection } from "./util/stepSectionStyle";
import { Z_RESTRICTION, roadClass, roadClassStroke } from "./util/roadClass";

export function getSDLaneRestrictionStyleNormal(feature) {
  return getSDLaneRestrictionColorStyle(feature);
}

export function getSDLaneRestrictionHovStyleNormal(feature) {
  return getSDLaneRestrictionHovColorStyle(feature);
}

export function getSDLaneRestrictionStyleClick(feature) {
  return getSDLaneRestrictionHovColorStyle(feature, true, false);
}

export function getSDLaneRestrictionStyleHover(feature) {
  return getSDLaneRestrictionHovColorStyle(feature, false, true);
}

// Shared shell for both SD families: normal draws the classified stroke,
// hover puts it on a white casing, click puts an orange core on it.
function toSDStyles(descriptor, geometry, click, hover) {
  // zIndex orders styles WITHIN the layer: a major road is drawn over a minor one where
  // they cross, and anything with a restriction over both. Defaults to Z_RESTRICTION so
  // a classified way always outranks plain road lines.
  const zIndex = descriptor.zIndex ?? Z_RESTRICTION;
  if (click) {
    return [
      new Style({
        geometry: geometry,
        zIndex: zIndex,
        stroke: new Stroke({ color: descriptor.color, width: 6 }),
      }),
      new Style({
        geometry: geometry,
        zIndex: zIndex,
        stroke: new Stroke({ color: "orange", width: 4 }),
      }),
    ];
  }
  if (hover) {
    return [
      new Style({
        geometry: geometry,
        zIndex: zIndex,
        stroke: new Stroke({ color: "white", width: 8 }),
      }),
      new Style({
        geometry: geometry,
        zIndex: zIndex,
        stroke: new Stroke({ color: descriptor.color, width: 4 }),
      }),
    ];
  }
  return [
    new Style({
      geometry: geometry,
      zIndex: zIndex,
      stroke: new Stroke({ color: descriptor.color, width: descriptor.width }),
    }),
  ];
}

function classifySDLaneRestrictionHov(tags, plain) {
  if (tags.rm_hovLanes) {
    return { color: "orange", width: 8 };
  }
  return plain;
}

function getSDLaneRestrictionHovColorStyle(
  feature,
  click = false,
  hover = false,
) {
  const plain = roadClassStroke(roadClass(feature));
  return styleBySection(
    feature,
    extractTags(feature),
    (tags) => classifySDLaneRestrictionHov(tags, plain),
    (descriptor, geometry) => toSDStyles(descriptor, geometry, click, hover),
  );
}

function isOnlyBusAllowed(tags) {
  if (
    tags.rm_vehicleAccessHgv &&
    tags.rm_vehicleAccessHgv.includes("no") &&
    tags.rm_vehicleAccessMotorcar &&
    tags.rm_vehicleAccessMotorcar.includes("no") &&
    tags.rm_vehicleAccessTaxi &&
    tags.rm_vehicleAccessTaxi.includes("no") &&
    !tags.rm_vehicleAccessBus
  ) {
    return true;
  }
  return false;
}

function isOnlyTaxiAndBusAllowed(tags) {
  if (
    tags.rm_vehicleAccessHgv &&
    tags.rm_vehicleAccessHgv.includes("no") &&
    tags.rm_vehicleAccessMotorcar &&
    tags.rm_vehicleAccessMotorcar.includes("no") &&
    !tags.rm_vehicleAccessBus &&
    !tags.rm_vehicleAccessTaxi
  ) {
    return true;
  }
  return false;
}

function isOnlyTaxiAllowed(tags) {
  if (
    tags.rm_vehicleAccessHgv &&
    tags.rm_vehicleAccessHgv.includes("no") &&
    tags.rm_vehicleAccessMotorcar &&
    tags.rm_vehicleAccessMotorcar.includes("no") &&
    tags.rm_vehicleAccessBus &&
    tags.rm_vehicleAccessBus.includes("no") &&
    !tags.rm_vehicleAccessTaxi
  ) {
    return true;
  }
  return false;
}

function isHgvNotAllowed(tags) {
  if (
    tags.rm_vehicleAccessHgv &&
    tags.rm_vehicleAccessHgv.includes("no") &&
    !tags.rm_vehicleAccessTaxi &&
    !tags.rm_vehicleAccessMotorcar &&
    !tags.rm_vehicleAccessBus
  ) {
    return true;
  }
  return false;
}

function isFullyClosed(tags) {
  if (
    tags.rm_vehicleAccessHgv &&
    tags.rm_vehicleAccessHgv.includes("no") &&
    tags.rm_vehicleAccessTaxi &&
    tags.rm_vehicleAccessTaxi.includes("no") &&
    tags.rm_vehicleAccessBus &&
    tags.rm_vehicleAccessBus.includes("no") &&
    tags.rm_vehicleAccessMotorcar &&
    tags.rm_vehicleAccessMotorcar.includes("no")
  ) {
    return true;
  }
  return false;
}

// bus ==> maroon, hgv ==> purple, psv ==> blue, taxi ==> yellow, hov ==> orange,
// motor_vehicle restriction ==> darkgrey, else grey
//
// The predicates read scalar values: a step-encoded access tag has already been
// resolved by styleBySection() to the value covering the section being drawn.
// That matters here — `"0-1000#no;5000-7000#yes".includes("no")` used to hold
// for the whole feature and painted the entire lane as restricted.
function classifySDLaneRestriction(tags, plain) {
  if (isOnlyBusAllowed(tags)) {
    return { color: "maroon", width: 8 };
  }
  if (isOnlyTaxiAndBusAllowed(tags)) {
    return { color: "blue", width: 8 };
  }
  if (isOnlyTaxiAllowed(tags)) {
    return { color: "yellow", width: 8 };
  }
  if (isHgvNotAllowed(tags)) {
    return { color: "purple", width: 8 };
  }
  if (isFullyClosed(tags)) {
    return { color: "darkgrey", width: 8 };
  }
  return plain;
}

function getSDLaneRestrictionColorStyle(feature, click = false, hover = false) {
  const plain = roadClassStroke(roadClass(feature));
  return styleBySection(
    feature,
    extractTags(feature),
    (tags) => classifySDLaneRestriction(tags, plain),
    (descriptor, geometry) => toSDStyles(descriptor, geometry, click, hover),
  );
}
